// Password dialogs: admin resets someone else's password, anyone changes their own.

import { App, Form, Input, Modal } from 'antd';
import { useState } from 'react';

import { conflictCode, errorText } from '@/api/errors';
import { ru } from '@/i18n/ru';

import { MIN_PASSWORD_LENGTH, changeOwnPassword, resetPassword } from './usersApi';

interface Values {
  current_password?: string;
  password: string;
  repeat: string;
}

interface Props {
  title: string;
  /** Ask for the current password (own password change). */
  withCurrent?: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (values: Values) => Promise<void>;
  successMessage: string;
}

function PasswordModal({ title, withCurrent, open, onClose, onSubmit, successMessage }: Props) {
  const [form] = Form.useForm<Values>();
  const { message } = App.useApp();
  const [saving, setSaving] = useState(false);

  function close() {
    form.resetFields();
    onClose();
  }

  async function submit() {
    let values: Values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      await onSubmit(values);
    } catch (error) {
      if (conflictCode(error) === 'invalid_password') {
        form.setFields([{ name: 'current_password', errors: [ru.users.wrongCurrent] }]);
      } else {
        void message.error(errorText(error));
      }
      return;
    } finally {
      setSaving(false);
    }
    void message.success(successMessage);
    close();
  }

  return (
    <Modal
      title={title}
      open={open}
      onCancel={close}
      onOk={() => void submit()}
      okText={ru.common.save}
      cancelText={ru.common.cancel}
      confirmLoading={saving}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" preserve={false} onFinish={() => void submit()}>
        {withCurrent ? (
          <Form.Item
            name="current_password"
            label={ru.users.currentPassword}
            rules={[{ required: true, message: ru.errors.required }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
        ) : null}
        <Form.Item
          name="password"
          label={ru.users.newPassword}
          rules={[
            { required: true, message: ru.errors.required },
            { min: MIN_PASSWORD_LENGTH, message: ru.errors.minLength(MIN_PASSWORD_LENGTH) },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="repeat"
          label={ru.users.repeatPassword}
          dependencies={['password']}
          rules={[
            { required: true, message: ru.errors.required },
            {
              validator: (_, value: string) =>
                !value || value === form.getFieldValue('password')
                  ? Promise.resolve()
                  : Promise.reject(new Error(ru.users.mismatch)),
            },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** Admin: set a new password for another account. */
export function ResetPasswordModal({
  user,
  onClose,
}: {
  user: { id: number; login: string } | null;
  onClose: () => void;
}) {
  return (
    <PasswordModal
      title={user ? ru.users.resetTitle(user.login) : ru.users.resetPassword}
      open={user !== null}
      onClose={onClose}
      successMessage={ru.users.resetDone}
      onSubmit={async (values) => {
        if (user) await resetPassword(user.id, values.password);
      }}
    />
  );
}

/** Any role: change your own password. */
export function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <PasswordModal
      title={ru.users.changeTitle}
      withCurrent
      open={open}
      onClose={onClose}
      successMessage={ru.users.changed}
      onSubmit={async (values) => {
        await changeOwnPassword(values.current_password ?? '', values.password);
      }}
    />
  );
}
